import { bumpRetry, listPending, listQueue, markFailed, markPending, QueuedRow, removeFromQueue } from "./queue";
import { replayRow } from "./replay";
import { isOnline } from "./isOnline";
import { isOfflineFeatureEnabled } from "./featureFlag";
import { publishCounters, OfflineDispatch } from "./counters";
import { generalActions } from "../backend/store/reducers/generalReducer";
import i18n from "../res/languages";
import { runPrefetchIfStale } from "./prefetch";
import { refreshAfterSync, targetsFromRows } from "./refreshAfterSync";
import { hasSession } from "./session";
import { overLimitFrom, OVER_LIMIT_EVENT } from "../utils/contactLimit";

const MAX_RETRIES = 5;

/**
 * How often an app that is left open and visible re-checks snapshot freshness.
 * Well under the 12-hour staleness threshold it is checking against, so a run is
 * never missed by more than an hour. Every tick is a timestamp comparison unless
 * the snapshot is genuinely stale.
 */
const FRESHNESS_CHECK_MS = 60 * 60 * 1000;

/**
 * Floor between top-ups started by the freshness triggers below.
 *
 * `isStale` treats `partial` and `failed` as stale, and both states persist until
 * a pass finally succeeds — so without this floor a phone stuck in `partial`
 * (one route 500ing, or storage restricted) would re-download every route each
 * time the volunteer switched back to the app. Far below the 12-hour staleness
 * threshold, so it never meaningfully delays a legitimate top-up.
 */
const MIN_TOP_UP_INTERVAL_MS = 15 * 60 * 1000;

let lastTopUpStartedAt = 0;

/** Test-only: clears the top-up floor so each test starts from a clean slate. */
export const __resetTopUpThrottle = (): void => {
  lastTopUpStartedAt = 0;
};

type Dispatch = OfflineDispatch;

export { publishCounters };

const statusOf = (error: any): number | undefined => error?.response?.status;

let flushing = false;

export const flushQueue = async (dispatch: Dispatch): Promise<void> => {
  // A second `online` event mid-flush must not start a parallel pass — that would
  // replay the same row twice concurrently and race the server-side dedupe.
  if (flushing) return;
  flushing = true;
  const replayed: QueuedRow[] = [];

  try {
    const pending = await listPending();

    for (const row of pending) {
      if (!isOnline()) break; // queue-blocking: the connection dropped mid-flush

      try {
        await replayRow(row);
        await removeFromQueue(row.id);
        replayed.push(row);
      } catch (error: any) {
        const status = statusOf(error);

        if (status === 401) {
          // Queue-blocking: every remaining row would fail identically. Keep them all
          // and ask for a re-login; the next `online` event resumes the flush.
          dispatch(
            generalActions.errorAC({
              message: i18n.t("offline.session_expired"),
              type: "error",
            })
          );
          break;
        }

        if (status === undefined) {
          // No response at all: the network died. Not this row's fault — stop and
          // wait rather than burning retries on every remaining row.
          break;
        }

        // View-only refusal (Task 16/17): SUBMIT_VISIT_REPORT and
        // UPDATE_HOUSEHOLD_CONTACT are the two writes the backend now guards
        // with 403 OVER_CONTACT_LIMIT once an account holds more contacts
        // than its plan covers. Unlike a genuine per-item 4xx (409, 422 —
        // handled below), this is NOT this row's fault, and it is NOT
        // retryable by fixing the row: every remaining row would fail
        // identically until the account is back within its plan. Treat it
        // like the queue-blocking cases above — keep the row queued (do NOT
        // markFailed, which would set it aside and, from the volunteer's
        // side, look just like a permanently lost visit report or edit) and
        // stop the run. Surface the reason via the same event the app-wide
        // banner/dialog already listen for, so whoever is looking at the
        // app right then sees why nothing is syncing.
        const overLimit = overLimitFrom(error);
        if (overLimit) {
          window.dispatchEvent(new CustomEvent(OVER_LIMIT_EVENT, { detail: overLimit }));
          dispatch(
            generalActions.errorAC({
              message: i18n.t("offline.over_limit"),
              type: "error",
            })
          );
          break;
        }

        if (status >= 500) {
          const retries = await bumpRetry(row.id);
          if (retries >= MAX_RETRIES) {
            await markFailed(row.id, `Server error ${status} after ${retries} attempts`);
          }
          break; // the server is unhealthy; don't hammer it with the rest of the queue
        }

        // Per-item 4xx (409 key reuse, 422 validation): set aside and keep going.
        await markFailed(row.id, `${status} ${error?.response?.data?.message ?? ""}`.trim());
      }
    }
  } finally {
    flushing = false;
    await publishCounters(dispatch);

    // A completed sync makes our cached copies wrong: they still show the stops we
    // just synced as unvisited. Re-pull — but only what was touched, and only once
    // the queue is actually empty.
    //
    // BOTH halves of this condition matter, in opposite directions:
    //
    // - Without "something replayed", every reconnect would refresh. flushQueue
    //   runs on the `online` event whether or not anything was queued.
    // - Without "queue emptied", a queue draining one row at a time against a
    //   failing server refreshes on every attempt. That is what was happening: a
    //   500 mid-flush left rows pending and still triggered a full re-download, and
    //   another on every retry. Measured in syncEngine.refreshTrigger.test.ts.
    //
    // The refresh is also targeted now rather than a whole prefetch pass. A sync
    // invalidates the routes whose stops were marked and the households that were
    // reported on — not the contact book, and not the details of every household in
    // the workspace, which is what re-running the full download meant.
    if (replayed.length > 0 && isOnline() && (await listPending()).length === 0) {
      void refreshAfterSync(targetsFromRows(replayed), dispatch);
    }
  }
};

/**
 * "Tap to retry" on a per-row badge. The badge knows the entityKey, not the row id,
 * and failed rows are excluded from listPending — so they have to be marked pending
 * again before a flush will pick them up.
 */
export const retryFailedByEntityKey = async (
  entityKey: string,
  dispatch: Dispatch
): Promise<void> => {
  const rows = await listQueue();
  const failed = rows.filter((r) => r.entityKey === entityKey && r.status === "failed");
  if (failed.length === 0) return;

  for (const row of failed) {
    await markPending(row.id);
  }
  await flushQueue(dispatch);
};

/**
 * Wires the flush to the browser's `online` event. Deliberately NOT the Background
 * Sync API: iOS Safari has never supported it and this app runs mostly on iPhones.
 * Returns an unsubscribe function.
 */
export const startSyncEngine = (dispatch: Dispatch): (() => void) => {
  // Freshness is checked at every moment the snapshot could have gone stale
  // without us noticing, not just at app start. `runPrefetchIfStale` is inert
  // unless the flag is on, the browser is online, no run is in flight, and the
  // snapshot is over 12 hours old — so in the common case each of these costs one
  // timestamp comparison.
  //
  // hasSession() is checked HERE rather than left to runPrefetchIfStale, which
  // does not check it. The 401 interceptor removes "user" but leaves "token", and
  // offline_enabled only clears on a clean 204 logout, so a revoked token would
  // otherwise fire a request on every visibility change, get bounced to /login,
  // and — a failed status being permanently stale — never settle.
  //
  // The MIN_TOP_UP_INTERVAL_MS floor is also enforced here rather than left to
  // runPrefetchIfStale: a snapshot stuck in `partial` (one route 500ing, or the
  // device is storage-restricted) is stale on every single check, so without this
  // floor a volunteer bouncing between this app and Maps would re-download every
  // route on every switch.
  const topUpIfStale = () => {
    if (!hasSession()) return;
    // isOnline() and isOfflineFeatureEnabled() are checked here as well as inside
    // runPrefetchIfStale — deliberately duplicated, so that a call which cannot
    // possibly run does not burn the floor below.
    //
    // The sequence that made this necessary: a volunteer in a dead zone wakes the
    // phone, `visibilitychange` fires, nothing runs because they are offline. Two
    // minutes later they drive back into coverage, `online` fires — and the top-up
    // is throttled out for the next thirteen minutes by a stamp written for a call
    // that never did anything. `flushQueue`'s post-replay refresh only fires when
    // rows actually replayed, so an empty queue refreshes nothing, which is exactly
    // the case the `online` trigger exists to close.
    if (!isOnline() || !isOfflineFeatureEnabled()) return;
    const now = Date.now();
    if (now - lastTopUpStartedAt < MIN_TOP_UP_INTERVAL_MS) return;
    lastTopUpStartedAt = now;
    void runPrefetchIfStale(dispatch);
  };

  const onOnline = () => {
    // Deliberately NOT session-gated: a flush that is skipped can strand a
    // volunteer's queued visit reports, which is far worse than the loop above.
    void flushQueue(dispatch);
    // flushQueue only re-pulls when rows actually replayed, so returning to signal
    // with an empty queue refreshed nothing before this line.
    topUpIfStale();
  };
  window.addEventListener("online", onOnline);

  // The primary trigger on a phone: fires when the volunteer picks the app back
  // up, which is exactly when freshness matters, and costs nothing while
  // backgrounded. Same pattern ImportProgressContext already uses for its TTL.
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") topUpIfStale();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  // Covers the app left open and visible — a desktop tab, or a phone propped up
  // all day — where visibilitychange never fires. iOS suspends this while
  // backgrounded, which is fine: the visibility handler covers the resume.
  const freshnessTimer = setInterval(topUpIfStale, FRESHNESS_CHECK_MS);

  // Boot case: the app may start online with rows left over from a previous session,
  // or start offline, in which case the counters still need to reach the banner.
  //
  // Both boot-time requests are gated on a live session, because this component tree
  // is also the router root for /login. Without a token every request 401s, the
  // response interceptor does window.location.replace("/login"), this remounts, and
  // it all happens again — a loop that locks the user out of the page they need in
  // order to recover. The flush reaches it through replayRow, which uses the same
  // axios instance and therefore the same interceptor; the prefetch reaches it on
  // its first request, and a `failed` status is always stale so it never settles.
  //
  // Only the BOOT calls are gated. A flush triggered by the `online` event above
  // still runs exactly as before: that fires during a live session, and gating it
  // could strand a volunteer's queued visit reports, which is far worse than the
  // loop. Nothing is dropped here either — the rows stay in IndexedDB, and the
  // flush that follows the next successful login picks them up.
  const session = hasSession();

  if (isOnline() && session) {
    void flushQueue(dispatch);
    // Background top-up: silent, and a no-op unless the snapshot is stale.
    //
    // Routed through topUpIfStale rather than calling runPrefetchIfStale directly,
    // so the boot pass both checks AND stamps the floor. `authStore.currentUser` is
    // not persisted and is written only by GET_USER_SUCCESS, so the effect in
    // useOfflineSyncEngine — keyed on the signed-in user id — fires once with
    // `undefined` at mount and again a few hundred ms later when getUser() resolves:
    // the engine is restarted on EVERY page load with a session. Calling directly,
    // the restart re-read the same status that had made pass A start (runPrefetchPass
    // writes its status only in its `finally`, so it is necessarily still stale),
    // reached runPrefetch with `running === true`, and set `rerunRequested` — one
    // more complete download of every route, the route list, the whole contact book,
    // categories, on cellular, on every app start.
    //
    // On a genuine first boot `lastTopUpStartedAt` is 0, so this always runs. The
    // same stamp also closes the hole where an app switch shortly after boot got an
    // unthrottled extra pass.
    topUpIfStale();
  } else {
    // Still publish: the banner has to be right on a cold start, and when there is
    // no session there is nothing to publish but zeroes anyway.
    void publishCounters(dispatch);
  }

  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    clearInterval(freshnessTimer);
  };
};
